import { HTMLTable } from "@blueprintjs/core"
import React from "react"
import { NavControlResult } from "../../../../../api/reconciliation"
import { BreachTables, BreachTableTitle } from "../Reconciliation.styled"
import {
  formatBp,
  formatNav4dp,
  formatNumber,
  formatPct,
  formatPrice,
  isBreach,
  navReferenceLabel,
  PriceGapBreaches,
} from "../reconciliation.utils"

interface ControlBreachesProps {
  // The NAV control per class as the operator sees it (operatorNavControls);
  // only those that did not pass are listed.
  navControls: NavControlResult[]
  // The price-gap breaches with the thresholds and the NAV reference the
  // control ran with (the hook's priceGapBreaches).
  priceGapBreaches?: PriceGapBreaches
}

/**
 * Every control that did not pass, as tables in the submit confirmation
 * (CONTEXT.md, "Acknowledgement"): per class, both NAVs, the gap and the
 * threshold; per security, the two quotes as each side writes them, the
 * two unit prices in fund currency the gap was read on (#1755), the gap,
 * the gap value and its share of the NAV. Unverified reads as such — it is
 * not a pass. Nothing when every control passed.
 */
const ControlBreaches: React.FC<ControlBreachesProps> = ({ navControls, priceGapBreaches }) => {
  const navRows = navControls.filter(isBreach)
  const priceRows = priceGapBreaches?.rows ?? []
  if (navRows.length === 0 && priceRows.length === 0) return null
  const navReference = priceGapBreaches?.navReference ?? 0
  return (
    <BreachTables>
      {navRows.length > 0 && (
        <section aria-label="NAV per share breaches">
          <BreachTableTitle>NAV per share</BreachTableTitle>
          <HTMLTable compact>
            <thead>
              <tr>
                <th>Class</th>
                <th className="num">Scient</th>
                <th className="num">Reference</th>
                <th className="num">Gap</th>
                <th className="num">Threshold</th>
              </tr>
            </thead>
            <tbody>
              {navRows.map(ctrl => (
                <tr key={ctrl.code}>
                  <td>{ctrl.code}</td>
                  <td className="num">{formatNav4dp(ctrl.nav_per_share_scient)}</td>
                  <td className="num">
                    {formatNav4dp(ctrl.nav_per_share_reference)}{" "}
                    <small>{navReferenceLabel(ctrl.reference)}</small>
                  </td>
                  <td className="num bad">
                    {ctrl.status === "unverified" ? "unverified" : formatBp(ctrl.gap_bp)}
                  </td>
                  <td className="num dim">{ctrl.threshold_bp} bp</td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
        </section>
      )}
      {priceRows.length > 0 && priceGapBreaches && (
        <section aria-label="Price gap breaches">
          <BreachTableTitle>
            Price gap (&gt; {priceGapBreaches.thresholds.price_gap_threshold_pct}% and &gt;{" "}
            {priceGapBreaches.thresholds.nav_share_threshold_pct}% of NAV)
          </BreachTableTitle>
          <HTMLTable compact>
            <thead>
              <tr>
                <th>Security</th>
                <th className="num">Scient quote</th>
                <th className="num">CACEIS quote</th>
                <th className="num">Scient (fund ccy)</th>
                <th className="num">CACEIS (fund ccy)</th>
                <th className="num">Gap</th>
                <th className="num">Gap value</th>
                <th className="num">% NAV</th>
              </tr>
            </thead>
            <tbody>
              {priceRows.map((row, i) => (
                <tr key={`${row.identifier}:${i}`}>
                  <td>
                    {row.identifier}
                    {row.description && row.description !== row.identifier ? (
                      <small> {row.description}</small>
                    ) : null}
                  </td>
                  <td className="num">{formatPrice(row.price_a)}</td>
                  <td className="num">{formatPrice(row.price_b)}</td>
                  <td className="num">{formatPrice(row.unit_price_a)}</td>
                  <td className="num">{formatPrice(row.unit_price_b)}</td>
                  <td className="num bad">
                    {row.price_gap_status === "unverified"
                      ? "unverified"
                      : formatPct(row.price_gap_pct)}
                  </td>
                  <td className="num">{formatNumber(row.price_gap_value)}</td>
                  <td className="num">
                    {navReference && row.price_gap_value != null
                      ? formatPct((row.price_gap_value / Math.abs(navReference)) * 100)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
        </section>
      )}
    </BreachTables>
  )
}

export default ControlBreaches
